/* TASK BOARD — the timeline (Gantt) view (Max, 30/10: "switch hay toggle để chuyển thành màn hình dạng timeline hay gantt
   chart để xem khoảng thời gian các task được thực hiện"). The same tasks and filters as the board, one row each, grouped
   by stage; a click opens the same popup.

   A bar runs from the REQUEST DATE (the day it was asked for) to:
     done            → the day it was finished (done_at, Vietnam time)
     has a due date  → the due date — and, still open after it, a red striped tail up to today
     No rush         → the planned date its doer chose (no tail: nobody asked for it by then)
     has an estimate → request date + estimated days
     nothing         → today, drawn faded with an open end: it is still running and nobody said for how long.
   Three scales: Days (the default — Max 30/10 "timeline nên là ngày": a column a day, weekends shaded), Weeks, Months. Colours = the board's stage colours, so a card and its bar match. */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import UserAvatar from './UserAvatar';

const DAY = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STAGES = ['backlog', 'todo', 'doing', 'review', 'done'];
const dn = (ymd) => Math.floor(Date.parse(`${String(ymd).slice(0, 10)}T00:00:00Z`) / DAY);   // a date as a day number
const vnDay = (iso) => (iso ? Math.floor((new Date(iso).getTime() + 7 * 3600000) / DAY) : null);
const fmt = (n) => { const d = new Date(n * DAY); return `${String(d.getUTCDate()).padStart(2, '0')}/${MONTHS[d.getUTCMonth()]}/${d.getUTCFullYear()}`; };
const isoWeek = (n) => {
  const d = new Date(n * DAY); const wd = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - wd + 3);
  const y0 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4)); return 1 + Math.round(((d - y0) / DAY - 3 + ((y0.getUTCDay() + 6) % 7)) / 7);
};

/* the next date of a repeating task — the same rule as taskBoard.js addPeriod: weeks by 7 days, months / quarters / years
   keep the series' day of month (anchor), clamped to the month's length */
export function addPeriod(s, freq, every = 1, anchor) {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (freq === 'week') return new Date(Date.UTC(y, m - 1, d + 7 * every)).toISOString().slice(0, 10);
  const mi = m - 1 + every * (freq === 'month' ? 1 : freq === 'quarter' ? 3 : 12);
  const yy = y + Math.floor(mi / 12); const mm = ((mi % 12) + 12) % 12;
  const last = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  return `${yy}-${String(mm + 1).padStart(2, '0')}-${String(Math.min(anchor || d, last)).padStart(2, '0')}`;
}
/* the occurrences still to come of an open repeating task, as day spans — drawn dashed on the timeline (up to `hi`) */
function ghostsOf(x, s, hi) {
  if (!x.repeatFreq || x.status === 'done' || !x.due) return [];
  const lead = s.end - s.start; const until = x.repeatUntil ? dn(x.repeatUntil) : Infinity;
  const out = []; let due = x.due;
  for (let i = 0; i < 24; i += 1) {
    due = addPeriod(due, x.repeatFreq, x.repeatEvery || 1, Number(x.due.slice(8, 10)));
    const e = dn(due); if (e > hi || e > until) break;
    out.push({ start: e - lead, end: e });
  }
  return out;
}

/* the span of one task, in day numbers, with how its end was decided */
export function spanOf(x, today) {
  const start = x.requested ? dn(x.requested) : vnDay(x.createdAt);
  let end; let kind;
  if (x.status === 'done' && x.doneAt) { end = vnDay(x.doneAt); kind = 'done'; }
  else if (x.due) { end = dn(x.due); kind = 'due'; }
  else if (x.noRush && x.planned) { end = dn(x.planned); kind = 'planned'; }   // No rush: the date its doer planned (Jay 30/10)
  else if (x.estDays > 0) { end = start + Math.ceil(x.estDays) - 1; kind = 'est'; }
  else { end = today; kind = 'open'; }
  if (end < start) end = start;
  const late = kind === 'due' && end < today ? today : null;     // still open past its due date
  return { start, end, kind, late };
}

export default function TaskTimeline({ t, tasks, onOpen, meId }) {
  const [scale, setScaleRaw] = useState(() => { try { return localStorage.getItem('tb.tlscale') || 'day'; } catch (e) { return 'day'; } });
  const setScale = (v) => { setScaleRaw(v); try { localStorage.setItem('tb.tlscale', v); } catch (e) { /* private window */ } };
  const [shut, setShut] = useState({});
  const [tip, setTip] = useState(null);         // { x, y, task, span }
  const today = vnDay(new Date().toISOString());
  const px = { day: 34, week: 16, month: 6 }[scale] || 34;
  const wd = t('xTask.tl_wd').split(',');        // Mon … Sun

  // open on today, a third from the left — the first look at week scale showed only September (30/10)
  const scrollRef = useRef(null);
  const hasRows = tasks.length > 0;
  const lead = useRef(0);
  useLayoutEffect(() => { const el = scrollRef.current; if (el) el.scrollLeft = Math.max(0, lead.current - (el.clientWidth - 300) / 3); }, [scale, hasRows]);
  const rows = useMemo(() => tasks.map((x) => ({ x, s: spanOf(x, today) })), [tasks, today]);
  const groups = useMemo(() => STAGES.map((st) => ({ st, rows: rows.filter((r) => r.x.status === st).sort((a, b) => a.s.start - b.s.start || a.s.end - b.s.end) }))
    .filter((g) => g.rows.length), [rows]);
  // the window: a few days before the first request, at least a week past the last end, and always today
  const lo0 = Math.min(today, ...rows.map((r) => r.s.start)); const hi0 = Math.max(today, ...rows.map((r) => r.s.late || r.s.end));
  const lo = lo0 - ((new Date(lo0 * DAY).getUTCDay() + 6) % 7) - 7;          // back to a Monday, one week of air
  const hi = Math.max(hi0 + 14, lo + ({ day: 35, week: 70, month: 150 }[scale] || 35));
  const days = hi - lo + 1;
  const width = days * px;
  const left = (n) => (n - lo) * px;
  lead.current = left(today);

  // header rows: Days = months over day numbers; Weeks = ISO weeks; Months = months
  const monthCells = [];
  for (let n = lo; n <= hi;) {
    const d = new Date(n * DAY); const next = dn(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString());
    const end = Math.min(next - 1, hi); monthCells.push({ n, w: (end - n + 1) * px, top: `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`, sub: '' }); n = end + 1;
  }
  const weekCells = [];
  for (let n = lo; n <= hi; n += 7) weekCells.push({ n, w: Math.min(7, hi - n + 1) * px, top: `W${isoWeek(n)}`, sub: fmt(n).slice(0, 6) });
  const dayCells = [];
  if (scale === 'day') for (let n = lo; n <= hi; n += 1) { const w = (new Date(n * DAY).getUTCDay() + 6) % 7; dayCells.push({ n, w: px, top: String(new Date(n * DAY).getUTCDate()), sub: wd[w] || '', end: w >= 5, mon: w === 0 }); }
  const tiers = scale === 'day' ? [monthCells, dayCells] : scale === 'week' ? [weekCells] : [monthCells];
  const lines = scale === 'day' ? dayCells : tiers[0];
  const weekends = [];
  if (scale !== 'month') for (let n = lo; n <= hi; n += 1) if ((new Date(n * DAY).getUTCDay() + 6) % 7 >= 5) weekends.push(n);
  const face = (x) => (x.assignee && x.assigneeId !== meId ? x.assignee : x.ownerId !== meId ? x.owner : null);
  const tipText = (x, s) => {
    const until = s.kind === 'open' ? t('xTask.tl_open') : fmt(s.end);
    const bits = [`${fmt(s.start)} → ${until}`, `${s.end - s.start + 1} ${t('xTask.tl_days')}`];
    if (x.estDays > 0) bits.push(`${t('xTask.f_est')}: ${t('xTask.estN', { n: x.estDays })}`);
    if (s.late) bits.push(t('xTask.tl_late', { n: s.late - s.end }));
    return bits;
  };

  return (
    <div className="tl-wrap">
      <div className="tl-tools">
        <div className="tl-seg" role="group">
          {['day', 'week', 'month'].map((k) => (
            <button key={k} type="button" className={scale === k ? 'on' : ''} onClick={() => setScale(k)} data-tl-scale={k}>{t(`xTask.tl_${k}`)}</button>
          ))}
        </div>
        <span className="tl-legend">
          {STAGES.map((st) => <span key={st} className={`tl-key c-${st}`}><i />{t(`xTask.col_${st}`)}</span>)}
          <span className="tl-key late"><i />{t('xTask.overdue')}</span>
          <span className="tl-key open"><i />{t('xTask.tl_open')}</span>
        </span>
        <span className="tl-rule">{t('xTask.tl_rule')}</span>
      </div>
      {!rows.length ? <p className="tb-hint">{t('xTask.noTasks')}</p> : (
        <div className="tl-scroll" ref={scrollRef} onMouseLeave={() => setTip(null)}>
          <div className="tl-grid" style={{ width: 300 + width }}>
            <div className="tl-head">
              <div className="tl-name tl-corner">{t('xTask.f_title')}</div>
              <div className="tl-cal" style={{ width }}>
                {tiers.map((cells, ti) => (
                  <div key={ti} className={`tl-tier${ti === tiers.length - 1 ? ' last' : ''}${scale === 'day' && ti === 1 ? ' days' : ''}`}>
                    {cells.map((h) => (
                      <div key={h.n} className={`tl-hcell${h.end ? ' wkend' : ''}${h.mon ? ' mon' : ''}${scale === 'day' && ti === 1 && h.n === today ? ' today' : ''}`} style={{ width: h.w }}
                        title={scale === 'day' && ti === 1 ? fmt(h.n) : undefined}>
                        {(h.w >= 56 || (scale === 'day' && ti === 1)) && <><b>{h.top}</b>{h.sub && <span>{h.sub}</span>}</>}
                      </div>
                    ))}
                  </div>
                ))}
                {scale !== 'day' && <span className="tl-today-tag" style={{ left: left(today) + px / 2 }}>{t('xTask.tl_today')}</span>}
              </div>
            </div>
            {groups.map((g) => (
              <React.Fragment key={g.st}>
                <button type="button" className={`tl-group c-${g.st}`} onClick={() => setShut((o) => ({ ...o, [g.st]: !o[g.st] }))} data-tl-group={g.st}>
                  <span className="tl-name"><span className="tl-caret">{shut[g.st] ? '▸' : '▾'}</span> {t(`xTask.col_${g.st}`)} <em>{g.rows.length}</em></span>
                </button>
                {!shut[g.st] && g.rows.map(({ x, s }) => {
                  const f = face(x);
                  return (
                    <div key={x.id} className="tl-row" data-tl-row={x.id}>
                      <button type="button" className="tl-name" onClick={() => onOpen(x.id)} title={x.title}>
                        <span className="tl-title">{x.title}</span>{f && <UserAvatar url={f.avatar} name={f.name} size={20} />}
                      </button>
                      <div className="tl-lane" style={{ width }}>
                        <button type="button" className={`tl-bar c-${x.status} k-${s.kind} p-${x.priority}`} data-tl-bar={x.id}
                          style={{ left: left(s.start), width: (s.end - s.start + 1) * px - 2 }}
                          onClick={() => onOpen(x.id)}
                          onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, task: x, span: s })}>
                          {scale !== 'month' && (s.end - s.start + 1) * px > 60 && x.estDays > 0 && <span>{t('xTask.estN', { n: x.estDays })}</span>}
                        </button>
                        {ghostsOf(x, s, hi).map((g) => (
                          <span key={g.end} className={`tl-ghost c-${x.status}`} style={{ left: left(g.start), width: (g.end - g.start + 1) * px - 2 }} title={`${t('xTask.tl_next')}: ${fmt(g.start)} → ${fmt(g.end)}`} />
                        ))}
                        {s.late && <span className="tl-late" style={{ left: left(s.end + 1), width: (s.late - s.end) * px - 2 }}
                          onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, task: x, span: s })} />}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {weekends.map((n) => <div key={`w${n}`} className="tl-wkband" style={{ left: 300 + left(n), width: px }} />)}
            {lines.map((h) => <div key={`l${h.n}`} className={`tl-vline${h.mon ? ' mon' : ''}`} style={{ left: 300 + left(h.n) }} />)}
            <div className="tl-today" style={{ left: 300 + left(today) + px / 2 }} />
          </div>
        </div>
      )}
      {tip && (
        <div className="tl-tip" style={{ left: Math.min(tip.x + 14, window.innerWidth - 300), top: tip.y + 16 }} role="tooltip">
          <b>{tip.task.title}</b>
          {tipText(tip.task, tip.span).map((l) => <div key={l}>{l}</div>)}
        </div>
      )}
    </div>
  );
}

